import * as formik from 'formik';
import * as yup from 'yup';
import { useState, useEffect } from 'react';
import { Modal, Button, InputGroup, Form, Row, Col } from 'react-bootstrap';
import { getRuleForm, editRule } from '../../utils/api';
import Loader from '../Loader';
import Success from '../Success'
import Error from '../Error'
import { faCirclePlus, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import InformationTooltip from '../InformationTooltip';

const EditRulesModal = ({ show, onHide, rule }) => {

    const [data, setData] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSaved, setIsSaved] = useState(false);

    const fetchData = async () => {
        setLoading(true);

        const data = await getRuleForm();
        if (data) {
            setData(data);
        }
        else {
            setError('Failed to fetch data');
        }
        setLoading(false);
    };

    const resetstateId = () => {
        setData({});
        setError(null);
        setLoading(false);
    };

    useEffect(() => {
        const onLoad = () => {
            if (show)
                fetchData();
            else
                resetstateId();
        }
        onLoad();
    }, [show]);

    const closeModal = (shouldRefresh) => {
        setLoading(true);
        setIsSaving(false);
        setError(false);
        setIsSaved(false);
        onHide(shouldRefresh);
    }



    const { Formik } = formik;
    const currencyRegex = /^(\$?(\d{1,3})(\d{3})*(\.\d{1,2})?)$/;

    const schema = yup.object().shape({
        stateId: yup.number().required('This field is required'),
        description: yup.string().required('This field is required'),
        processTradeIn: yup.bool().required('This field is required'),
        maxPurchasePrice: yup.number().nullable(),
        maxTrailerPurchasePrice: yup.number().nullable(),
        maxPurchaseQty: yup.number().integer().required('This field is required'),
        maxSaleQty: yup.number().integer(),
        maxTradeIn: yup.number().integer().nullable(),
        maxGvw: yup.number().integer().nullable(),
        maxOverMatch: yup.number().nullable(),
        maxUnderMatch: yup.number().nullable(),
        limitedToSameClass: yup.bool().required('This field is required'),
        limitedToSameStateTitling: yup.bool().required('This field is required'),
        prioritizeNewOverUsedVehicles: yup.bool().required('This field is required'),
        purchaseInXDays: yup.number().integer().nullable(),
        minLeaseLength: yup.number().integer().nullable(),
        seedPoolSize: yup.number().required('This field is required').min(1).max(20),
        salePriorityOrderId: yup.number().required('This field is required'),
        purchasePriorityOrderId: yup.number().required('This field is required'),
    });



    return (
        <Modal show={show} onHide={onHide} size="xl" backdrop={true} style={{ zIndex: 1051 }} dialogClassName="modal-90w" centered>
            <Modal.Header closeButton>
                <Modal.Title>{'Edit Rule (Id = ' + rule?.Id + ")"}</Modal.Title>
            </Modal.Header>

            {loading || isSaving ? (
                <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <Loader showText={true} text={isSaving && 'Saving...'} />
                </div>
            ) : (
                error ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Error />
                    </div>
                ) : isSaved ? (
                    <div style={{ height: 230, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <Success />
                    </div>
                ) : (
                    <Modal.Body>
                        <Formik
                            validationSchema={schema}
                            onSubmit={async (form) => {
                                form.id = rule?.Id;

                                setLoading(true);
                                const res = await editRule(form);
                                if (res) {
                                    setIsSaved(true);
                                }
                                else {
                                    setError(true);
                                }
                                setIsSaving(false);
                                setLoading(false);

                                if (res)
                                    setTimeout(() => closeModal(true), 2000);
                            }}
                            initialValues={{
                                stateId: rule?.State?.Id,
                                description: rule?.Description,
                                processTradeIn: rule?.ProcessTradeIn?.toString(),
                                maxPurchasePrice: rule?.MaxPurchasePrice?.toFixed(2),
                                maxTrailerPurchasePrice: rule?.MaxTrailerPurchasePrice?.toFixed(2),
                                maxPurchaseQty: rule?.MaxPurchaseQty?.toFixed(0),
                                maxSaleQty: rule?.MaxSaleQty?.toFixed(0),
                                maxTradeIn: rule?.MaxTradeIn?.toFixed(0),
                                maxGvw: rule?.MaxGvw?.toFixed(2),
                                maxOverMatch: rule?.MaxOverMatch?.toFixed(2),
                                maxUnderMatch: rule?.MaxUnderMatch?.toFixed(2),
                                limitedToSameClass: rule?.LimitedToSameClass?.toString(),
                                limitedToSameStateTitling: rule?.LimitedToSameStateTitling?.toString(),
                                prioritizeNewOverUsedVehicles: rule?.PrioritizeNewOverUsedVehicles?.toString(),
                                purchaseInXDays: rule?.PurchaseInXDays?.toFixed(0),
                                minLeaseLength: rule?.MinLeaseLength?.toFixed(0),
                                seedPoolSize: rule?.SeedPoolSize?.toFixed(0),
                                salePriorityOrderId: rule?.SalePriorityOrder.Id?.toFixed(0),
                                purchasePriorityOrderId: rule?.PurchasePriorityOrder.Id?.toFixed(0)
                            }}
                        >
                            {({ handleSubmit, handleChange, values, touched, errors }) => (

                                <Form noValidate onSubmit={handleSubmit}>
                                    <Row>
                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='stateId' value={values.stateId} onChange={handleChange} isInvalid={errors.stateId} isValid={values.stateId && !errors.stateId} disabled aria-label="State" >
                                                    <option key={rule?.State?.Id}>{rule?.State?.Name}</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.stateId}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="stateId"><span style={{ color: 'red' }}>*</span>State</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "StateId")?.Description} style={{ marginTop: 10 }} />
                                        </Col>
                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='processTradeIn' value={values.processTradeIn} onChange={handleChange} isInvalid={errors.processTradeIn} isValid={values.processTradeIn && !errors.processTradeIn} aria-label="Process Trade In" >
                                                    <option value={false}>No</option>
                                                    <option value={true}>Yes</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.processTradeIn}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="processTradeIn"><span style={{ color: 'red' }}>*</span>Process Trade In</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "ProcessTradeIn")?.Description} style={{ marginTop: 10 }} />
                                        </Col>
                                        <Col xs={6} sm={6} md={6} lg={6}>
                                            <InputGroup>
                                                <Form.Control id="description" value={values.description} onChange={handleChange} isInvalid={errors.description} isValid={values.description && !errors.description} aria-label="Description" />
                                                <Form.Control.Feedback type='invalid'>{errors.description}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="description"><span style={{ color: 'red' }}>*</span>Description</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "Description")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxPurchasePrice" value={values.maxPurchasePrice} onChange={handleChange} isInvalid={errors.maxPurchasePrice} isValid={values.maxPurchasePrice && !errors.maxPurchasePrice} aria-label="Max Purchase Price" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxPurchasePrice}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxPurchasePrice">Max Purchase Price</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxPurchasePrice")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxTrailerPurchasePrice" value={values.maxTrailerPurchasePrice} onChange={handleChange} isInvalid={errors.maxTrailerPurchasePrice} isValid={values.maxTrailerPurchasePrice && !errors.maxTrailerPurchasePrice} aria-label="Max Trailer Purchase Price" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxTrailerPurchasePrice}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxTrailerPurchasePrice">Max Trailer Purchase Price</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxTrailerPurchasePrice")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxPurchaseQty" value={values.maxPurchaseQty} onChange={handleChange} isInvalid={errors.maxPurchaseQty} isValid={values.maxPurchaseQty && !errors.maxPurchaseQty} aria-label="Max Purchase Qty" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxPurchaseQty}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxPurchaseQty"><span style={{ color: 'red' }}>*</span>Max Purchase Qty</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxPurchaseQty")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxSaleQty" value={values.maxSaleQty} onChange={handleChange} isInvalid={errors.maxSaleQty} isValid={values.maxSaleQty && !errors.maxPrice} aria-label="Max Sale Qty" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxSaleQty}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxSaleQty">Max Sale Qty</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxSaleQty")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxTradeIn" value={values.maxTradeIn} onChange={handleChange} isInvalid={errors.maxTradeIn} isValid={values.maxTradeIn && !errors.maxTradeIn} aria-label="Max Trade In" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxTradeIn}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxTradeIn">Max Trade In</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxTradeIn")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxGvw" value={values.maxGvw} onChange={handleChange} isInvalid={errors.maxGvw} isValid={values.maxGvw && !errors.maxGvw} aria-label="Max Gross Vehicle Weight" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxGvw}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxGvw">Max Gross Vehicle Weight</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxGVW")?.Description} style={{ marginTop: 10 }} />
                                        </Col>


                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxOverMatch" value={values.maxOverMatch} onChange={handleChange} isInvalid={errors.maxOverMatch} isValid={values.maxOverMatch && !errors.maxOverMatch} aria-label="Max Over Match" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxOverMatch}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxOverMatch">Max Over Match</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxOverMatch")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="maxUnderMatch" value={values.maxUnderMatch} onChange={handleChange} isInvalid={errors.maxUnderMatch} isValid={values.maxUnderMatch && !errors.maxUnderMatch} aria-label="Max Under Match" />
                                                <Form.Control.Feedback type='invalid'>{errors.maxUnderMatch}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="maxUnderMatch">Max Under Match</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MaxUnderMatch")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='limitedToSameClass' value={values.limitedToSameClass} onChange={handleChange} isInvalid={errors.limitedToSameClass} isValid={values.limitedToSameClass && !errors.limitedToSameClass} aria-label="Limited To Same Class" >
                                                    <option value={false}>No</option>
                                                    <option value={true}>Yes</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.limitedToSameClass}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="limitedToSameClass"><span style={{ color: 'red' }}>*</span>Limited To Same Class</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "LimitedToSameClass")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='limitedToSameStateTitling' value={values.limitedToSameStateTitling} onChange={handleChange} isInvalid={errors.limitedToSameStateTitling} isValid={values.limitedToSameStateTitling && !errors.limitedToSameStateTitling} aria-label="Limited To Same State Titling" >
                                                    <option value={false}>No</option>
                                                    <option value={true}>Yes</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.limitedToSameStateTitling}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="limitedToSameStateTitling"><span style={{ color: 'red' }}>*</span>Limited To Same State Titling</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "LimitedToSameStateTitling")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='prioritizeNewOverUsedVehicles' value={values.prioritizeNewOverUsedVehicles} onChange={handleChange} isInvalid={errors.prioritizeNewOverUsedVehicles} isValid={values.prioritizeNewOverUsedVehicles && !errors.prioritizeNewOverUsedVehicles} aria-label="Prioritize New Over Used Vehicles" >
                                                    <option value={false}>No</option>
                                                    <option value={true}>Yes</option>
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.prioritizeNewOverUsedVehicles}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="prioritizeNewOverUsedVehicles"><span style={{ color: 'red' }}>*</span>Prioritize New Over Used Vehicles</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "PrioritizeNewOverUsedVehicles")?.Description} style={{ marginTop: 10 }} />
                                        </Col>


                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="purchaseInXDays" value={values.purchaseInXDays} onChange={handleChange} isInvalid={errors.purchaseInXDays} isValid={values.purchaseInXDays && !errors.purchaseInXDays} aria-label="Purchase In X Days" />
                                                <Form.Control.Feedback type='invalid'>{errors.purchaseInXDays}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="purchaseInXDays">Purchase In X Days</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "PurchaseInXDays")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="minLeaseLength" value={values.minLeaseLength} onChange={handleChange} isInvalid={errors.minLeaseLength} isValid={values.minLeaseLength && !errors.minLeaseLength} aria-label="Min Lease Length" />
                                                <Form.Control.Feedback type='invalid'>{errors.minLeaseLength}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="minLeaseLength">Min Lease Length</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "MinLeaseLength")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Control id="seedPoolSize" value={values.seedPoolSize} onChange={handleChange} isInvalid={errors.seedPoolSize} isValid={values.seedPoolSize && !errors.seedPoolSize} aria-label="Seed Pool Size" />
                                                <Form.Control.Feedback type='invalid'>{errors.seedPoolSize}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="seedPoolSize"><span style={{ color: 'red' }}>*</span>Seed Pool Size</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "SeedPoolSize")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='salePriorityOrderId' value={values.salePriorityOrderId} onChange={handleChange} isInvalid={errors.salePriorityOrderId} isValid={values.salePriorityOrderId && !errors.salePriorityOrderId} aria-label="Sale Priority Order" >
                                                    <option value="">Select a Sale Priority Order</option>
                                                    {data?.SalePriorityOrders?.map((o, index) => (
                                                        <option key={o.Id} value={o.Id}>{o.Description}</option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.salePriorityOrderId}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="salePriorityOrderId"><span style={{ color: 'red' }}>*</span>Sale Priority Order</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "SalePriorityOrderId")?.Description} style={{ marginTop: 10 }} />
                                        </Col>

                                        <Col xs={3} sm={3} md={3} lg={3}>
                                            <InputGroup>
                                                <Form.Select id='purchasePriorityOrderId' value={values.purchasePriorityOrderId} onChange={handleChange} isInvalid={errors.purchasePriorityOrderId} isValid={values.purchasePriorityOrderId && !errors.purchasePriorityOrderId} aria-label="Purchase Priority Order" >
                                                    <option value="">Select a Purchase Priority Order</option>
                                                    {data?.PurchasePriorityOrders?.map((o, index) => (
                                                        <option key={o.Id} value={o.Id}>{o.Description}</option>
                                                    ))}
                                                </Form.Select>
                                                <Form.Control.Feedback type='invalid'>{errors.purchasePriorityOrderId}</Form.Control.Feedback>
                                            </InputGroup>
                                            <Form.Label className="mb-3" style={{ fontSize: 10 }} htmlFor="purchasePriorityOrderId"><span style={{ color: 'red' }}>*</span>Purchase Priority Order</Form.Label>
                                            <InformationTooltip text={data?.RuleMetadata?.find(c=>c.ColumnName === "PurchasePriorityOrderId")?.Description} style={{ marginTop: 10 }} />
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col className="d-flex justify-content-end">
                                            <Button type="submit">Save</Button>
                                        </Col>
                                    </Row>
                                </Form>
                            )}
                        </Formik>
                    </Modal.Body>
                ))}
        </Modal>
    );
};

export default EditRulesModal;